import MultipleOccupancyBreakdown from './multiple-occupancy-breakdown';
import MultipleOccupancyBreakdownV2 from './multiple-occupancy-breakdown-v2';

import OccupancyBreakdown from './occupancy-breakdown';
import OccupancyBreakdownV2 from './occupancy-breakdown-v2';

import OccupancySummary from './occupancy-summary';

export default class OccupancyMain {
    constructor(engineForm, isCorporate, initial = {}) {
        if (engineForm === null) {
            return;
        }

        this._mainContainer = document.querySelector(
            '.js-roi-search-engine-occupancy-container'
        );

        if (this._mainContainer === null) {
            return;
        }

        this._MAX_ROOMS = 4;
        this._engineForm = engineForm;

        let _dataPrefixes;
        try {
            _dataPrefixes = this._engineForm.dataset.destinationPrefixes;
        } catch (e) {
            _dataPrefixes = this._engineForm.getAttribute('data-destination-prefixes');
        }
        this._destinationPrefixes = (_dataPrefixes) ? JSON.parse(_dataPrefixes) : [];

        const isSingleOccupancy = !!(this._engineForm
                .querySelector('.js-roi-search-engine-single-occupancy')),
            isOccupancyDropdown = !!(this._engineForm
                .querySelector('.roi-search-engine-occupancy--v2'));

        if (isOccupancyDropdown === false) {
            this._initV1(isSingleOccupancy);
        } else if (isOccupancyDropdown) {
            this._initV2(isSingleOccupancy);
        }

        this._occupancySummary = new OccupancySummary(this);
        this._initValues(isCorporate, initial);
        this._openBreakdown();
        this._blockSubmitWithMissingAges();
    }

    getEngineForm() { return this._engineForm; }

    getHotelData() { return this._hotelData; }

    getMaxRooms() { return this._MAX_ROOMS; }

    updateSummary(selectedOccupancies, roomCounter) {
        this._occupancySummary.updateSummary(selectedOccupancies, roomCounter);
    }

    _initV1(isSingleOccupancy) {
        if (isSingleOccupancy === false) {
            this._occupancyBreakdown = new MultipleOccupancyBreakdown(this);
        } else {
            this._occupancyBreakdown = new OccupancyBreakdown(this);
        }
    }

    _initV2(isSingleOccupancy) {
        if (isSingleOccupancy === false) {
            this._occupancyBreakdown = new MultipleOccupancyBreakdownV2(this);
        } else {
            this._occupancyBreakdown = new OccupancyBreakdownV2(this);
        }
    }

    _initValues(isCorporate, initial) {
        try {
            this._occupancyData = JSON.parse(this._mainContainer.dataset.occupancyData);
        } catch (e) {
            this._occupancyData = JSON.parse(this._mainContainer.getAttribute('data-occupancy-data'));
        }
        initial = this._checkOccupancies(this._mainContainer, initial);

        if (isCorporate) {
            let destinationCodeField = this._engineForm.querySelector(
                '.js-roi-search-engine-destination-code'
            );
            if (!destinationCodeField) {
                destinationCodeField = this._engineForm.querySelector(
                    '#id_coddestino'
                );
            }
            this._resetOccupancies(destinationCodeField.value.trim(), initial || true);
            destinationCodeField.addEventListener('input', (e) => {
                this._resetOccupancies(e.currentTarget.value, initial);
            });
        } else {
            this._resetOccupancies(Object.keys(this._occupancyData)[0], initial || true);
        }
    }

    _checkOccupancies(mainContainer, initial) {
        const urlOccupancies = new URL(window.location.href).searchParams
            .get('occupancies');

        if (urlOccupancies) {
            try {
                const decoded = decodeURIComponent(urlOccupancies),
                    parsedOccupancies = JSON.parse(decoded);

                if (Array.isArray(parsedOccupancies) && parsedOccupancies.length > 0) {
                    // Return the complete array of occupancies from the URL
                    return parsedOccupancies;
                }
            } catch (e) {
                console.warn('Failed to parse occupancies parameter:', e);
            }
        }

        // Fallback to NewDispoParams cookie if occupancies parameter doesn't exist in URL
        const cookieOccupancies = OccupancyMain._getCookieOccupancies();
        if (cookieOccupancies) {
            return cookieOccupancies;
        }

        // Fallback to individual adults and children-ages parameters
        this._checkAdults(mainContainer, initial);
        this._checkChildren(mainContainer, initial);
        return initial;
    }

    static _getCookieOccupancies() {
        try {
            const cookieValue = OccupancyMain._getCookieByName('NewDispoParams');
            if (!cookieValue) {
                return null;
            }

            const urlDecoded = OccupancyMain._decodeMany(cookieValue, 3),
                normalized = OccupancyMain._decodeBackslashOctal(urlDecoded),
                outer = JSON.parse(OccupancyMain._safeJsonParse(normalized));

            if (!outer) { return null; }

            const occ = outer.occupancies;

            return OccupancyMain._decodedOccupanciesFromCookie(occ);
        } catch (e) {
            console.warn('Failed to parse NewDispoParams cookie:', e);
        }
        return null;
    }

    static _safeJsonParse(str) {
        try {
            return JSON.parse(str);
        } catch (e) {
            return null;
        }
    }

    static _decodeMany(value, max = 3) {
        let v = value;
        for (let i = 0; i < max; i++) {
            try {
                const next = decodeURIComponent(v);
                if (next === v) { break; }
                v = next;
            } catch (e) {
                break;
            }
        }
        return v;
    }

    static _decodeBackslashOctal(str) {
        // \054 -> ',' ; generaliza por si hay más octales
        return str.replace(/\\([0-7]{3})/g, (_, oct) => String.fromCharCode(parseInt(oct, 8)));
    }

    // eslint-disable-next-line complexity
    static _decodedOccupanciesFromCookie(occ) {
        if (typeof occ === 'string') {
            const occDecoded = OccupancyMain._decodeBackslashOctal(
                    OccupancyMain._decodeMany(occ, 3)
                ),
                occParsed = OccupancyMain._safeJsonParse(occDecoded);

            if (Array.isArray(occParsed) && occParsed.length > 0) { return occParsed; }
            const unwrapped = OccupancyMain._safeJsonParse(occDecoded);

            if (Array.isArray(unwrapped) && unwrapped.length > 0) { return unwrapped; }
        }

        if (Array.isArray(occ) && occ.length > 0) {
            return occ;
        }

        return occ;
    }

    static _getCookieByName(name) {
        const nameEQ = `${name}=`,
            cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length);
            }
        }
        return null;
    }

    _checkChildren(mainContainer, initial) {
        const childreAges = new URL(window.location.href).searchParams.get('children-ages');

        if (childreAges) {
            this._children = childreAges;
        } else {
            try {
                this._children = mainContainer.dataset.occupancyInitialAges;
            } catch (e) {
                this._children = mainContainer.getAttribute('data-occupancy-initial-aages');
            }
        }

        if (this._children) {
            initial.ages = this._children.replaceAll(',', ';');
            initial.children = this._children.split(',').length;
        }
        return initial;
    }

    _checkAdults(mainContainer, initial) {
        const adults = new URL(window.location.href).searchParams.get('adults');

        if (adults) {
            this._adults = adults;
        } else {
            try {
                this._adults = mainContainer.dataset.occupancyInitialAdults;
            } catch (e) {
                this._adults = mainContainer.getAttribute('data-occupancy-initial-adults');
            }
        }


        if (this._adults) {
            initial.adults = parseInt(this._adults, 10);
        }
        return initial;
    }

    static _isValidInitialValue(initial) {
        const isValidArray = Array.isArray(initial) && initial.length > 0,
            isValidObject = typeof initial === 'object' &&
                !Array.isArray(initial) &&
                Object.keys(initial).length > 0;
        return isValidArray || isValidObject;
    }

    _getDefaultInitialValue() {
        if (!this._hotelData) {
            return false;
        }
        return {
            adults: Object.keys(this._hotelData.allowedOccupancies)[0]
        };
    }

    _resetOccupancies(hotelCode, initial = false) {
        if (this._destinationPrefixes.includes(hotelCode[0])) {
            this._hotelData = this._getCombinedOccupancyDataForDestination(hotelCode);
            this._mainContainer.style.display = 'none';
            this._occupancyBreakdown.launch(initial);
            return;
        }

        if (hotelCode && hotelCode !== 'todos' && hotelCode !== 'undefined') {
            this._mainContainer.style.display = 'flex';
        } else {
            this._mainContainer.style.display = 'none';
        }

        this._hotelData = this._occupancyData[hotelCode];

        if (!OccupancyMain._isValidInitialValue(initial)) {
            initial = this._getDefaultInitialValue();
        }

        this._occupancyBreakdown.launch(initial);
    }

    /**
     * Retrieves for a given destination the maximum allowed children for each
     * allowed adults occupancy of all hotels, as well as the maximum children
     * age of all of them
     */
    _getCombinedOccupancyDataForDestination(destinationCode) {
        const destinationHotels = this._engineForm.querySelectorAll(
                `[data-destination="${destinationCode}"]`
            ),
            data = {
                allowedOccupancies: {},
                childrenMaxAge: 0
            };
        destinationHotels.forEach((hotel) => {
            let hotelData;
            try {
                hotelData = this._occupancyData[hotel.dataset.value];
            } catch (e) {
                hotelData = this._occupancyData[hotel.getAttribute('data-value')];
            }
            data.childrenMaxAge = Math.max(data.childrenMaxAge, hotelData.childrenMaxAge);
            Object.keys(hotelData.allowedOccupancies).forEach((adults) => {
                if (data.allowedOccupancies.hasOwnProperty(adults)) {
                    data.allowedOccupancies[adults] = Math.max(
                        data.allowedOccupancies[adults], hotelData.allowedOccupancies[adults]
                    );
                } else {
                    data.allowedOccupancies[adults] = hotelData.allowedOccupancies[adults];
                }
            });
        });
        return data;
    }

    /**
     * Captured so that it runs before the other submit listeners of the
     * engine: a search with an age still to be chosen is neither sent nor
     * tracked. Here, and not in the engine widget, so that the standalone
     * occupancies bundle blocks it too
     */
    _blockSubmitWithMissingAges() {
        this._engineForm.addEventListener('submit', (event) => {
            if (this._hasMissingAges()) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        }, true);
    }

    _hasMissingAges() {
        // A hidden occupancy (no hotel or a destination) has no ages to choose
        if (this._mainContainer.style.display === 'none') {
            return false;
        }
        return this._occupancyBreakdown.hasMissingAges();
    }

    _openBreakdown() {
        this._occupancySummary.getSummary().addEventListener('click', () => {
            const calendar = document.querySelector('#roicalendar');

            if (calendar !== null && calendar.classList.contains('show-calendar')) {
                calendar.classList.remove('show-calendar');
            }
            this._occupancyBreakdown.open();
        });
    }
}
